import { Pencil, Trash2, X, Check, MapPin } from "lucide-react";
import { useState } from "react";
import { WORKPLACE_TYPES } from "../lib/workplaceTypes";

/**
 * One row in the sites list. Handles its own edit state so SitesManager stays
 * focused on the collection-level actions.
 */
export default function SiteRow({ site, onRename, onChangeType, onChangeClient, onDelete, onOpenLocation }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(site.name);

  const save = async () => {
    const ok = await onRename(site.id, name.trim());
    if (ok) setEditing(false);
  };

  const cancel = () => {
    setEditing(false);
    setName(site.name);
  };

  return (
    <div
      className="flex items-center gap-3 py-2 border-b border-gray-100"
      data-testid={`site-row-${site.id}`}
    >
      <div
        className="w-3 h-8"
        style={{ background: site.color.bg, borderLeft: `3px solid ${site.color.border}` }}
      />
      {editing ? (
        <>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 border border-gray-300 px-2 py-1 rounded-sm text-sm"
            autoFocus
          />
          <button onClick={save} className="text-green-700 p-1" aria-label="Save"><Check className="w-4 h-4" /></button>
          <button onClick={cancel} className="text-gray-500 p-1" aria-label="Cancel"><X className="w-4 h-4" /></button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm font-medium truncate">{site.name}</span>
          <input
            placeholder="Client"
            defaultValue={site.client_name || ""}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (site.client_name || "")) onChangeClient(site.id, v || null);
            }}
            data-testid={`site-client-input-${site.id}`}
            className="border border-gray-300 px-2 py-1 rounded-sm text-xs w-32"
            title="Client this site belongs to (used by per-client Excel filter)"
          />
          <select
            data-testid={`site-type-select-${site.id}`}
            value={site.type || ""}
            onChange={(e) => onChangeType(site.id, e.target.value)}
            className="border border-gray-300 px-2 py-1 rounded-sm bg-white text-xs font-medium"
            title="Workplace type"
          >
            <option value="">— Type —</option>
            {WORKPLACE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <button
            onClick={() => onOpenLocation(site)}
            className={`p-1 rounded-sm ${
              site.latitude != null && site.longitude != null
                ? "text-green-700 hover:text-green-900"
                : "text-gray-400 hover:text-[#002FA7]"
            }`}
            data-testid={`pin-site-${site.id}`}
            title={
              site.latitude != null && site.longitude != null
                ? `Location set (${site.latitude.toFixed(4)}, ${site.longitude.toFixed(4)})`
                : "Pin location"
            }
            aria-label="Pin location"
          >
            <MapPin className="w-4 h-4" />
          </button>
          <button
            onClick={() => setEditing(true)}
            className="text-gray-500 hover:text-gray-900 p-1"
            data-testid={`edit-site-${site.id}`}
            aria-label="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(site.id)}
            className="text-red-600 hover:text-red-800 p-1"
            data-testid={`delete-site-${site.id}`}
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
}
